// utils/colors.js — Single source of truth for all UI colors

// ── Backgrounds ──────────────────────────────────────────────────────────────
export const BG_BASE     = '#0A0A0A';  // deepest backgrounds, track fills
export const BG_SURFACE  = '#1A1A1A';  // card surfaces
export const BG_ELEVATED = '#2A2A2A';  // elevated panels, popovers

// ── Brand ────────────────────────────────────────────────────────────────────
export const BRAND_PURPLE = '#BB86FC'; // primary interactive, buttons, links
export const BRAND_TEAL   = '#03DAC6'; // secondary accent
export const BRAND_ORANGE = '#E8875A'; // opportunity / pipeline

// ── Semantic score scale (use ONLY these for good/warn/poor) ─────────────────
export const COLOR_GOOD = '#03DAC6';   // teal-green
export const COLOR_WARN = '#FFCF44';   // amber
export const COLOR_POOR = '#CF6679';   // rose

// ── Text ─────────────────────────────────────────────────────────────────────
export const TEXT_PRIMARY   = '#FFFFFF';
export const TEXT_SECONDARY = 'rgba(255,255,255,0.7)';
export const TEXT_DISABLED  = 'rgba(255,255,255,0.5)';
export const HEADER_TEXT    = 'rgba(255,255,255,0.75)'; // soft light gray for card header text

// ── Borders ──────────────────────────────────────────────────────────────────
export const BORDER_CARD    = 'rgba(255,255,255,0.12)';
export const BORDER_SECTION = 'rgba(255,255,255,0.08)';
export const BORDER_ACTIVE  = 'rgba(187,134,252,0.5)';

// ── Chips (neutral — differentiation from label, not color) ──────────────────
export const CHIP_BG   = 'rgba(255,255,255,0.12)';
export const CHIP_TEXT = 'rgba(255,255,255,0.7)';

// ── Data visualization series ─────────────────────────────────────────────────
export const VIZ = {
  primary:   BRAND_PURPLE,
  secondary: COLOR_GOOD,
  tertiary:  COLOR_WARN,
  negative:  COLOR_POOR,
  neutral:   'rgba(255,255,255,0.08)',
};

// ── Categorical palettes ──────────────────────────────────────────────────────
// DISC personality dimensions
export const DISC_COLORS = {
  D: COLOR_POOR,    // rose   — Dominance
  I: COLOR_WARN,    // amber  — Influence
  S: COLOR_GOOD,    // teal   — Steadiness
  C: BRAND_PURPLE,  // purple — Conscientiousness
};

// StrengthsFinder domains
export const DOMAIN_COLORS = {
  'Strategic Thinking':    BRAND_PURPLE,
  'Executing':             COLOR_GOOD,
  'Influencing':           COLOR_WARN,
  'Relationship Building': COLOR_POOR,
};

// ── COLORS object — drop-in replacement for all local component COLORS consts ─
export const COLORS = {
  cardBg:     BG_SURFACE,
  cardBorder: BORDER_SECTION,
  bgDark:     BG_BASE,
  bgElevated: BG_ELEVATED,
  text:       TEXT_PRIMARY,
  textMuted:  TEXT_SECONDARY,
  green:      COLOR_GOOD,
  teal:       COLOR_GOOD,
  amber:      COLOR_WARN,
  red:        COLOR_POOR,
  purple:     BRAND_PURPLE,
  blue:       BRAND_PURPLE,   // blue retired — use purple
  cyan:       COLOR_GOOD,     // cyan retired — use teal/good
  pink:       COLOR_POOR,     // pink retired — use rose/poor
  orange:     BRAND_ORANGE,   // muted orange — opportunity / pipeline
};

// ── Helper functions ──────────────────────────────────────────────────────────

/** Score color for a 0–10 scale. 7+ = good, 5+ = warn, <5 = poor */
export const getScoreColor = (score) =>
  score >= 7 ? COLOR_GOOD : score >= 5 ? COLOR_WARN : COLOR_POOR;

/** Priority/severity badge color */
export const getPriorityColor = (level) => {
  const l = (level || '').toLowerCase();
  if (l === 'high' || l === 'critical' || l === 'red')     return COLOR_POOR;
  if (l === 'medium' || l === 'moderate' || l === 'amber') return COLOR_WARN;
  return COLOR_GOOD;
};

/** Card header color by entity type */
export const getCardHeaderColor = (cardType) => {
  switch ((cardType || '').toLowerCase()) {
    case 'employee':    return BRAND_PURPLE;
    case 'project':     return COLOR_GOOD;
    case 'client':      return COLOR_WARN;
    case 'engagement':  return BRAND_PURPLE;
    case 'report':      return COLOR_POOR;
    case 'opportunity': return BRAND_ORANGE;
    default:            return TEXT_DISABLED;
  }
};

/** Status color (active/complete/at-risk etc.) */
export const getStatusColor = (status) => {
  if (!status) return TEXT_DISABLED;
  const s = status.toLowerCase();
  if (s.includes('active') || s.includes('in progress'))                        return COLOR_GOOD;
  if (s.includes('complete') || s.includes('done'))                             return BRAND_PURPLE;
  if (s.includes('plan') || s.includes('not start') || s.includes('soft book')) return COLOR_WARN;
  if (s.includes('hold') || s.includes('block') || s.includes('delay'))         return COLOR_POOR;
  return TEXT_DISABLED;
};

export const getProjectStatusColor = getStatusColor;

/** Trend arrow color */
export const getTrendColor = (trend) => {
  if (!trend) return TEXT_DISABLED;
  switch (trend.toLowerCase()) {
    case 'increasing': return COLOR_GOOD;
    case 'decreasing': return COLOR_POOR;
    default:           return COLOR_WARN;
  }
};

/** Neutral chip style object for MUI sx prop */
export const chipStyle = (semantic) => {
  if (semantic === 'good') return { bgcolor: `${COLOR_GOOD}20`, color: COLOR_GOOD, border: `1px solid ${COLOR_GOOD}40` };
  if (semantic === 'warn') return { bgcolor: `${COLOR_WARN}20`, color: COLOR_WARN, border: `1px solid ${COLOR_WARN}40` };
  if (semantic === 'poor') return { bgcolor: `${COLOR_POOR}20`, color: COLOR_POOR, border: `1px solid ${COLOR_POOR}40` };
  return { bgcolor: CHIP_BG, color: CHIP_TEXT };
};
